/**
 * Таблица элементов.
 *
 * Плотная, с сортировкой по колонкам и без визуального шума: строк на экране
 * должно помещаться много. На узком экране превращается в карточки
 * (документ «Структура интерфейса», §21).
 */

import { ArrowDownIcon, ArrowUpIcon, ChevronsUpDownIcon, FlagIcon } from 'lucide-react';
import type { ItemSummaryDto } from '@project-os/contracts';
import { ITEM_TYPE_LABELS_RU, PRIORITY_LABELS_RU } from '@project-os/contracts';
import { ItemStatusBadge } from '@/components/common/status-badge';
import { UserAvatar } from '@/components/common/user-avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatAge, formatDate, formatRelative } from '@/lib/format';
import { cn } from '@/lib/utils';
import { ItemTypeIcon } from './item-icon';

const PRIORITY_STYLES: Record<string, string> = {
  CRITICAL: 'text-tone-danger font-medium',
  HIGH: 'text-tone-warning',
  MEDIUM: 'text-muted-foreground',
  LOW: 'text-muted-foreground/70',
};

export interface ItemsTableProps {
  items: ItemSummaryDto[];
  selectedId: string | null;
  onSelect: (itemId: string) => void;
  sort: string;
  direction: 'asc' | 'desc';
  onSortChange: (field: string) => void;
  /** Плотность строк. Компактная помещает вдвое больше на экран. */
  density?: 'normal' | 'compact';
}

function SortableHead({
  field,
  label,
  sort,
  direction,
  onSortChange,
  className,
}: {
  field: string;
  label: string;
  sort: string;
  direction: 'asc' | 'desc';
  onSortChange: (field: string) => void;
  className?: string;
}) {
  const active = sort === field;
  const Icon = !active ? ChevronsUpDownIcon : direction === 'asc' ? ArrowUpIcon : ArrowDownIcon;

  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onSortChange(field)}
        className={cn(
          '-mx-1 inline-flex items-center gap-1 rounded px-1 hover:text-foreground',
          active && 'text-foreground',
        )}
        aria-label={`Сортировать по полю «${label}»`}
      >
        {label}
        <Icon className={cn('size-3', active ? 'opacity-100' : 'opacity-40')} aria-hidden />
      </button>
    </TableHead>
  );
}

export function ItemsTable({
  items,
  selectedId,
  onSelect,
  sort,
  direction,
  onSortChange,
  density = 'normal',
}: ItemsTableProps) {
  // Компактный режим — та же таблица с плотными строками: список, в который
  // помещается вдвое больше строк, отвечает на вопрос «что вообще есть».
  const dense = density === 'compact';
  const cellClass = dense ? '[&_td]:py-1' : '';

  return (
    <>
      {/* Таблица — основное представление на десктопе */}
      <div
        className={cn(
          'hidden md:block [&_td:first-child]:pl-6 [&_td:last-child]:pr-6 [&_th:first-child]:pl-6 [&_th:last-child]:pr-6',
          cellClass,
        )}
      >
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className={dense ? 'w-28' : 'w-36'}>Тип</TableHead>
              <SortableHead
                field="title"
                label="Название"
                sort={sort}
                direction={direction}
                onSortChange={onSortChange}
              />
              <SortableHead
                field="status"
                label="Статус"
                sort={sort}
                direction={direction}
                onSortChange={onSortChange}
                className="w-40"
              />
              <TableHead className="hidden w-40 lg:table-cell">Направление</TableHead>
              <TableHead className={dense ? 'w-32' : 'w-40'}>Ответственный</TableHead>
              <SortableHead
                field="priority"
                label="Приоритет"
                sort={sort}
                direction={direction}
                onSortChange={onSortChange}
                className="hidden w-28 xl:table-cell"
              />
              <SortableHead
                field="createdAt"
                label="Возраст"
                sort={sort}
                direction={direction}
                onSortChange={onSortChange}
                className="hidden w-24 lg:table-cell"
              />
              <SortableHead
                field="updatedAt"
                label="Изменён"
                sort={sort}
                direction={direction}
                onSortChange={onSortChange}
                className={dense ? 'w-28' : 'w-32'}
              />
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow
                key={item.id}
                onClick={() => onSelect(item.id)}
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelect(item.id);
                  }
                }}
                className={cn(
                  'cursor-pointer',
                  selectedId === item.id && 'bg-accent',
                  item.archivedAt && 'opacity-55',
                )}
              >
                <TableCell>
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <ItemTypeIcon type={item.type} className="size-3.5" />
                    <span className="truncate">{typeLabel(item)}</span>
                  </span>
                </TableCell>
                <TableCell>
                  <span className="flex items-center gap-1.5">
                    {item.isNextAction ? (
                      <FlagIcon
                        className="size-3.5 shrink-0 text-primary"
                        aria-label="Следующее действие"
                      />
                    ) : null}
                    <span className="min-w-0 truncate font-medium">{item.title}</span>
                  </span>
                  {item.dueDate && !dense ? (
                    <span className="mt-0.5 block text-2xs text-muted-foreground">
                      Срок: {formatDate(item.dueDate)}
                    </span>
                  ) : null}
                </TableCell>
                <TableCell>
                  <ItemStatusBadge type={item.type} status={item.status} />
                </TableCell>
                <TableCell className="hidden text-xs text-muted-foreground lg:table-cell">
                  <span className="block truncate">{item.workstream?.name ?? '—'}</span>
                </TableCell>
                <TableCell>
                  {item.owner ? (
                    <span className="flex items-center gap-1.5">
                      <UserAvatar user={item.owner} size="sm" />
                      <span className="truncate text-xs">{item.owner.name}</span>
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground/70">не назначен</span>
                  )}
                </TableCell>
                <TableCell
                  className={cn(
                    'hidden text-xs xl:table-cell',
                    item.priority ? PRIORITY_STYLES[item.priority] : 'text-muted-foreground/70',
                  )}
                >
                  {item.priority ? PRIORITY_LABELS_RU[item.priority] : '—'}
                </TableCell>
                <TableCell
                  className="hidden text-xs text-muted-foreground tabular-nums lg:table-cell"
                  title={`Создан ${formatDate(item.createdAt)}`}
                >
                  {formatAge(item.createdAt)}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {formatRelative(item.updatedAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Карточки — на узком экране */}
      <ul className="divide-y md:hidden">
        {items.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              onClick={() => onSelect(item.id)}
              className="w-full px-4 py-3 text-left hover:bg-accent"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <ItemTypeIcon type={item.type} className="size-3.5" />
                  {typeLabel(item)}
                </span>
                <ItemStatusBadge type={item.type} status={item.status} />
              </div>
              <p className="mt-1 font-medium">{item.title}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {item.owner?.name ?? 'Без ответственного'} · {formatRelative(item.updatedAt)}
              </p>
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}

/** Подпись типа берётся из реестра — второго списка типов в коде нет. */
function typeLabel(item: ItemSummaryDto): string {
  return ITEM_TYPE_LABELS_RU[item.type];
}
